import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, PackageOpen, Gauge, Link2Off } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { useAsyncJob } from '@/hooks/useAsyncJob';
import { supabase } from '@/integrations/supabase/client';

interface ScoreJobRow {
  id: string;
  // useAsyncJob's underlying query filters `.in('status', ['queued', 'processing'])`
  // (src/hooks/useAsyncJob.ts:48), so this hook can structurally never deliver a
  // terminal status here — a job that finishes or fails simply drops out of the
  // poll result. Terminal status is resolved separately below via a direct
  // one-off read, which has its own (wider) status type.
  status: 'queued' | 'processing';
  scored_products: number;
  total_products: number;
}

export interface FeedCheckAnalyzingProps {
  feedId: string;
  /**
   * Beschriftung der Aktion im Leerzustand (nie ein Score-Job gelaufen).
   *
   * Ohne sie ist dieser Zustand eine Sackgasse: er ist der einzige Inhalt der
   * Seite, solange kein Snapshot existiert, und trug bis dahin keinen einzigen
   * Knopf. Der Aufrufer entscheidet, welche Handlung dem gemessenen Stand der
   * Kette entspricht — diese Komponente kennt nur den Score-Job.
   */
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  /**
   * WP5 A2O-16: welche Handlung der Knopf anbietet. Ueberschrift und Text
   * folgen ihr; vorher stand "Starte deine erste Optimierung" ueber einem
   * Knopf "Produkte laden".
   */
  emptyKind?: 'score' | 'connect' | 'load' | null;
}

/** The 6 real scoring components (labor illusion must show REAL work — research
 * G5/P21). Scoring runs all 6 per product simultaneously, so we deliberately
 * show a static list + the real product counter instead of a fake step
 * sequence. Weights mirror _shared/scoring.ts SCORING_WEIGHTS. */
const DIMENSION_KEYS = ['title', 'description', 'details', 'highlights', 'gpc', 'attributes'] as const;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// process-score-queue flips score_calculation_jobs.status to 'completed' BEFORE
// it paginates product_scores (1000 rows/page) and upserts feed_health_snapshots
// (supabase/functions/process-score-queue/index.ts) — for large feeds that write
// can lag the status flip by several seconds. Invalidating the snapshots query
// right on 'completed' can therefore race ahead of the write, refetch empty, and
// permanently strand the user on the analyzing screen (settledRef only lets this
// resolution fire once per job, so nothing ever retries). We poll for the
// snapshot row to actually land before invalidating.
const SNAPSHOT_POLL_INTERVAL_MS = 1500;
const SNAPSHOT_POLL_MAX_ATTEMPTS = 20; // ~30s of headroom above the pagination-driven write gap

/** Resolves once a `feed_health_snapshots` row exists for the feed, or once the
 * bounded retry budget is exhausted (whichever comes first). FeedCheckAnalyzing
 * only ever mounts while the feed has zero snapshot rows (its parent gates on
 * `!current`, derived from this same table), so "any row appeared" reliably
 * means "the job we were watching finished writing its snapshot" — no need to
 * correlate by job id. If the budget runs out (e.g. a job completed with zero
 * scored products, so no snapshot will ever be written) we give up and let the
 * caller proceed anyway rather than poll forever. */
async function waitForSnapshotRow(feedId: string, isCancelled: () => boolean): Promise<void> {
  for (let attempt = 0; attempt < SNAPSHOT_POLL_MAX_ATTEMPTS; attempt++) {
    if (isCancelled()) return;
    const { data } = await supabase
      .from('feed_health_snapshots')
      .select('id')
      .eq('feed_id', feedId)
      .limit(1)
      .maybeSingle();
    if (data) return;
    if (attempt < SNAPSHOT_POLL_MAX_ATTEMPTS - 1) {
      await sleep(SNAPSHOT_POLL_INTERVAL_MS);
    }
  }
}

export function FeedCheckAnalyzing({ feedId, emptyActionLabel, onEmptyAction, emptyKind = null }: FeedCheckAnalyzingProps) {
  const { t } = useTranslation('optimization');
  const queryClient = useQueryClient();
  // Own channel name: useScoringJob's 'progress-modal-score-job' channel is
  // taken by the (mounted) progress modal on this page.
  const { activeJob } = useAsyncJob<ScoreJobRow>({
    tableName: 'score_calculation_jobs',
    channelName: 'feed-check-analyzing-score-job',
    queryKeyPrefix: 'feed-check-analyzing-score-job',
    feedId,
    forcePolling: true,
  });

  const isActive = !!activeJob;

  // The poll can only ever show us 'queued'/'processing' rows (see ScoreJobRow
  // above), so "a job we were watching just disappeared from the poll" is the
  // only completion signal available here — and it's ambiguous: a finished job
  // and a failed job both vanish from the result the same way. We resolve that
  // ambiguity with one direct terminal-status read of the job row before
  // deciding whether to reveal the report or show an error. `wasActiveRef`
  // remembers we actually saw a job run (so a feed that simply has no job yet
  // never triggers this, and so the render below never flashes the noScores
  // fallback during the async gap while we're resolving); `settledRef` makes a
  // single job's resolution fire once, and is reset whenever a new job becomes
  // active so a second job for the same feed (component stayed mounted) is
  // resolved independently of the first.
  const wasActiveRef = useRef(false);
  const settledRef = useRef(false);
  // Caches the last real job row while activeJob is set, so the render below
  // can keep showing the last known progress (e.g. "3700 von 3751 geprüft")
  // during the completion-resolution window instead of falling back to a
  // fabricated "Analyse startet" / 0% state right after real progress was
  // already on screen.
  const lastJobRef = useRef<ScoreJobRow | null>(null);
  const [justFinished, setJustFinished] = useState(false);
  const [failed, setFailed] = useState(false);

  // Defensive reset if feedId ever changes without an unmount. Currently
  // unreachable in practice (FeedSwitcher is hidden in feed-check mode, so
  // this component only ever mounts once per feed) but cheap to guard against
  // carrying stale completion/failure state into a different feed. Skips its
  // own initial mount run: this effect fires after the FIRST render commits
  // too, which would otherwise wipe out the lastJobRef cache set below
  // synchronously during that same first render (a real risk, not just a
  // test artifact, if the very first activeJob is already truthy and the
  // job completes before any other render happens).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    wasActiveRef.current = false;
    settledRef.current = false;
    lastJobRef.current = null;
    setJustFinished(false);
    setFailed(false);
  }, [feedId]);

  if (activeJob) {
    lastJobRef.current = activeJob;
  }

  useEffect(() => {
    if (isActive) {
      wasActiveRef.current = true;
      settledRef.current = false;
      // No-ops if already false — React bails out of the re-render for a
      // primitive setState with an unchanged value, so this is safe to call
      // unconditionally on every activation without extra dependencies.
      setJustFinished(false);
      setFailed(false);
      return;
    }
    if (!wasActiveRef.current || settledRef.current) return;
    settledRef.current = true;

    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('score_calculation_jobs')
        .select('status')
        .eq('feed_id', feedId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      // Only a confirmed 'failed' or 'cancelled' status shows the error
      // state (both are terminal-without-a-report; check-job-failures and
      // process-score-queue can produce either). Any other outcome
      // (completed, or an inconclusive read) falls back to the original
      // "assume finished" behavior — a false negative here just reproduces
      // the old UX, a false positive would wrongly block the report, which
      // is worse.
      if (!error && (data?.status === 'failed' || data?.status === 'cancelled')) {
        setFailed(true);
        return;
      }

      // Wait for the backend's snapshot write to actually land before
      // revealing the report — see waitForSnapshotRow for why this matters.
      await waitForSnapshotRow(feedId, () => cancelled);
      if (cancelled) return;

      setJustFinished(true);
      queryClient.invalidateQueries({ queryKey: ['feed-health-all-snapshots', feedId] });
    })();

    return () => {
      cancelled = true;
    };
  }, [isActive, feedId, queryClient]);

  if (failed) {
    return (
      <ErrorState
        title={t('feedCheck.analyzing.failedTitle')}
        description={t('feedCheck.analyzing.failedDescription')}
        onRetry={() => window.location.reload()}
      />
    );
  }

  // wasActiveRef gates the fallback so the async window between "the watched
  // job just disappeared" and "we've confirmed why" never flashes noScores —
  // it can only be false here for a feed that genuinely never had a job.
  if (!isActive && !justFinished && !wasActiveRef.current) {
    const icon = emptyKind === 'connect' ? Link2Off : emptyKind === 'score' ? Gauge : PackageOpen;
    return (
      <EmptyState
        icon={icon}
        title={emptyKind ? t(`feedCheck.analyzing.empty.${emptyKind}Title`) : t('noScores')}
        description={emptyKind ? t(`feedCheck.analyzing.empty.${emptyKind}Desc`) : t('noScoresDesc')}
        actionLabel={emptyActionLabel}
        onAction={onEmptyAction}
        actionVariant="gradient"
      />
    );
  }

  // Use the cached last-known job during the completion-resolution window
  // (activeJob has already gone null but justFinished/failed haven't
  // resolved yet) so the counters never regress to a fabricated "starting"
  // state right after showing real progress.
  const displayJob = activeJob ?? lastJobRef.current;
  const total = Number(displayJob?.total_products) || 0;
  const scored = Number(displayJob?.scored_products) || 0;
  const pct = total > 0 ? Math.min(100, Math.round((scored / total) * 100)) : 0;

  return (
    <Card
      style={{
        background: 'hsl(var(--primary) / 0.06)',
        border: '1px solid hsl(var(--primary) / 0.12)',
        boxShadow: 'var(--shadow-md)',
      }}
    >
      <CardContent className="pt-6 pb-6 space-y-5">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-primary animate-spin" />
          <h2 className="text-lg font-heading font-bold">{t('feedCheck.analyzing.title')}</h2>
        </div>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {/* Vorgabe 3.3: nie "0 von N" — bis zum ersten gescorten Produkt
                bleibt es bei der Startaussage. */}
            {total > 0 && scored > 0
              ? t('feedCheck.analyzing.progress', { scored, total })
              : t('feedCheck.analyzing.starting')}
          </p>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all duration-700"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <div>
          <span className="text-[11px] font-bold tracking-[0.08em] text-primary mb-2 block">
            {t('feedCheck.analyzing.dimensionsTitle')}
          </span>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {DIMENSION_KEYS.map((key) => (
              <li key={key} className="text-sm text-muted-foreground flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                {t(`feedCheck.analyzing.dimensions.${key}`)}
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
